import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

const COLLAPSE_KEY = 'cashflow-nav-collapsed';

interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
  children?: { label: string; path: string }[];
  adminOnly?: boolean;
  roles?: string[];
}

const navItems: NavItem[] = [
  {
    label: 'Validation Board',
    path: '/validation',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
      </svg>
    ),
  },
  {
    label: 'Entities',
    path: '/entities',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3" />
      </svg>
    ),
  },
  {
    label: 'Audit',
    path: '/audit',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    roles: ['ADMIN', 'GROUP_FINANCE'],
  },
  {
    label: 'Help',
    path: '/help',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    label: 'Admin',
    path: '/admin',
    adminOnly: true,
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    children: [
      { label: 'System Dashboard', path: '/admin/dashboard' },
      { label: 'CF Codes', path: '/admin/cf-codes' },
      { label: 'Entities', path: '/admin/entities' },
      { label: 'Statement Layout', path: '/admin/layout' },
      { label: 'Users', path: '/admin/users' },
      { label: 'Exchange Runs', path: '/admin/exchange-runs' },
      { label: 'Engagement', path: '/admin/engagement' },
    ],
  },
];

function NavSection({
  item,
  collapsed,
  onNavigate,
  onExpandRail,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
  onExpandRail?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!item.children) {
    return (
      <NavLink
        to={item.path}
        end={item.path === '/'}
        onClick={onNavigate}
        title={item.label}
        className={({ isActive }) =>
          clsx(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            collapsed && 'lg:justify-center',
            isActive
              ? 'bg-primary-600/20 text-primary-300'
              : 'text-slate-300 hover:bg-slate-800 hover:text-white',
          )
        }
      >
        {item.icon}
        <span className={clsx(collapsed && 'lg:hidden')}>{item.label}</span>
      </NavLink>
    );
  }

  return (
    <div>
      <button
        onClick={() => {
          // When collapsed on desktop, first expand the rail so the submenu is usable.
          if (collapsed) onExpandRail?.();
          setExpanded((v) => !v);
        }}
        title={item.label}
        className={clsx(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-white',
          collapsed && 'lg:justify-center',
        )}
        aria-expanded={expanded}
      >
        {item.icon}
        <span className={clsx('flex-1 text-left', collapsed && 'lg:hidden')}>{item.label}</span>
        <svg
          className={clsx(
            'h-4 w-4 transition-transform',
            expanded && 'rotate-90',
            collapsed && 'lg:hidden',
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {expanded && (
        <div className={clsx('ml-8 mt-1 space-y-1', collapsed && 'lg:hidden')}>
          {item.children.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              onClick={onNavigate}
              className={({ isActive }) =>
                clsx(
                  'block rounded-lg px-3 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'bg-primary-600/20 text-primary-300 font-medium'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
                )
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop-only icon-rail collapse; remembered across sessions.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const visibleItems = navItems.filter((item) => {
    if (item.adminOnly) return isAdmin;
    if (item.roles && user) return item.roles.includes(user.role);
    return true;
  });

  // `collapsed` drives lg:-prefixed classes only, so the mobile drawer is always
  // shown in full regardless of the desktop collapse state.
  const sidebarContent = (
    <div className="flex h-full flex-col bg-navy-900">
      {/* Logo */}
      <div
        className={clsx(
          'flex items-center gap-3 border-b border-slate-700 px-4 py-5',
          collapsed && 'lg:justify-center lg:px-2',
        )}
      >
        <img src="/logo.png" alt="Ashcourt" className="h-6 w-auto flex-shrink-0" />
        <div className={clsx(collapsed && 'lg:hidden')}>
          <h1 className="text-sm font-bold text-white">Ashcourt Cashflow</h1>
          <p className="text-xs text-slate-400">Month-end reporting</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" aria-label="Main navigation">
        {visibleItems.map((item) => (
          <NavSection
            key={item.path}
            item={item}
            collapsed={collapsed}
            onNavigate={() => setMobileOpen(false)}
            onExpandRail={() => setCollapsed(false)}
          />
        ))}
      </nav>

      {/* User area */}
      <div className="border-t border-slate-700 p-4">
        {user && (
          <div className={clsx('flex items-center gap-3', collapsed && 'lg:flex-col lg:gap-2')}>
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-600/20 text-sm font-medium text-primary-300"
              title={collapsed ? `${user.name ?? user.email}` : undefined}
            >
              {user.name?.[0]?.toUpperCase() ?? user.email[0].toUpperCase()}
            </div>
            <div className={clsx('min-w-0 flex-1', collapsed && 'lg:hidden')}>
              <p className="truncate text-sm font-medium text-white">
                {user.name ?? user.email}
              </p>
              <p className="truncate text-xs text-slate-400" title={user.email}>{user.email}</p>
            </div>
            <Link
              to="/account/change-password"
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
              title="Change password"
              aria-label="Change password"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </Link>
            <button
              onClick={handleLogout}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
              title="Sign out"
              aria-label="Sign out"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed left-4 top-4 z-40 rounded-lg bg-white p-2 shadow-md lg:hidden"
        aria-label="Open navigation"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 border-r border-slate-700 transition-all duration-200',
          'lg:relative lg:translate-x-0 lg:z-auto',
          collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Sidebar"
      >
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute right-2 top-4 lg:hidden"
          aria-label="Close navigation"
        >
          <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {sidebarContent}

        {/* Collapse toggle — straddles the sidebar's right edge, mid-height (desktop only) */}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed}
          className="absolute right-0 top-1/2 z-30 hidden h-7 w-7 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-slate-600 bg-navy-800 text-slate-300 shadow-md transition-colors hover:bg-slate-700 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 lg:flex"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" aria-hidden={true} />
          ) : (
            <ChevronLeft className="h-4 w-4" aria-hidden={true} />
          )}
        </button>
      </aside>
    </>
  );
}
